import { useCallback, useRef, useState } from 'react';

interface StoredItem<T> {
  value: T;
  version: string;
}

function getStorageValue<T>(key: string, version: string, initialValue?: T) {
  try {
    if (typeof localStorage === 'undefined') {
      return initialValue;
    }

    const item = localStorage.getItem(key);
    if (!item) return initialValue;

    const parsed: StoredItem<T> = JSON.parse(item);

    // 버전이 다르면 초기값 반환
    if (parsed.version !== version) {
      localStorage.removeItem(key);
      return initialValue;
    }

    return parsed.value;
  } catch (error) {
    console.error('Error reading localStorage key:', key, error);
    return initialValue;
  }
}

function setStorageValue<T>(key: string, version: string, value: T): boolean {
  try {
    if (typeof localStorage === 'undefined') {
      return true;
    }

    const item: StoredItem<T> = { version, value };
    localStorage.setItem(key, JSON.stringify(item));
    return true;
  } catch (error) {
    console.error('Error setting localStorage key:', key, error);
    return false;
  }
}

function removeStorageValue(key: string) {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing localStorage key:', key, error);
  }
}

export default function useLocalStorage<T>(key: string, initialValue?: T, version = 'v1.0') {
  const initialValueRef = useRef(initialValue);
  const [storedValue, setStoredValue] = useState<T | undefined>(() => {
    return getStorageValue(key, version, initialValue);
  });

  const setValue = useCallback(
    (value: T | ((prevState: T | undefined) => T)) => {
      setStoredValue(_prev => {
        // functional updater일 때 localStorage 최신값 기준으로 계산하여 cross-instance 덮어쓰기 방지
        const baseValue =
          typeof value === 'function'
            ? getStorageValue(key, version, initialValueRef.current)
            : undefined;
        const newValue =
          typeof value === 'function'
            ? (value as (prevState: T | undefined) => T)(baseValue)
            : value;

        const didPersist = setStorageValue(key, version, newValue);
        if (!didPersist) {
          return _prev;
        }

        return newValue;
      });
    },
    [key, version],
  );

  const removeValue = useCallback(() => {
    removeStorageValue(key);
    setStoredValue(undefined);
  }, [key]);

  return [storedValue, setValue, removeValue] as const;
}
